// TODO: Codes related to showing flag is commented because this view is currently wrong and asked to hide this view and keep code for future
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ActiveTrainState, ConfigKey, SimpleBounds } from '@/types';
import type { Layer } from '@deck.gl/core';
import type { MapRef } from 'react-map-gl/mapbox';

import useGetCurrentZoom from '@/hooks/use-get-current-zoom';
import { useMapConfig } from '@/hooks/use-map-config';
import { CATEGORY_TO_CONFIG_KEY, ZOOM_8 } from '@/utils/constants/map';
import { doBoundsIntersect } from '@/utils/helpers/train-simulation/do-bounds-intersect';
import { useLockedTrainCleanup } from '@/utils/helpers/train-simulation/use-locked-train-cleanup';
import { useProcessSocketData } from '@/utils/helpers/train-simulation/use-process-socket-data';
import { useTrainAnimationLoop } from '@/utils/helpers/train-simulation/use-train-animation-loop';
import { useTrainMapEvents } from '@/utils/helpers/train-simulation/use-train-map-events';
import { useTrainPinAnimation } from '@/utils/helpers/train-simulation/use-train-pin-animation';

export const useRealtimeTrains = (
  mapRef: MapRef | null | undefined,
  lockedJourneyId: string | number | null
  // flaggedTrainIds?: string[]
): Layer[] => {
  const map = mapRef?.getMap();
  const { config } = useMapConfig();
  const { currentZoom } = useGetCurrentZoom();

  const [isSocketReady, setIsSocketReady] = useState(false);

  const trainStateMap = useRef<Map<string, ActiveTrainState>>(new Map());
  const visibleTrainIdsRef = useRef<string[]>([]);
  const visibleTrainIdSetRef = useRef<Set<string>>(new Set());
  const visibleBoundsRef = useRef<SimpleBounds | null>(null);

  const isInteractingRef = useRef(false);
  const shouldForceRenderRef = useRef(false);
  const hasRenderedDataRef = useRef(false);

  const lockedIdRef = useRef(lockedJourneyId);

  const updateVisibleTrainsCache = useCallback(() => {
    const bounds = visibleBoundsRef.current;

    if (!bounds) {
      return;
    }

    const lockedId = lockedIdRef.current;
    const activeIds: string[] = [];
    const lockedIdStr = lockedId ? String(lockedId) : null;

    for (const train of trainStateMap.current.values()) {
      const trainIdStr = String(train.id);
      const isLocked = lockedIdStr !== null && trainIdStr === lockedIdStr;

      const configKey = CATEGORY_TO_CONFIG_KEY[train.journeyCategory];
      const isCategoryEnabled = configKey ? config[configKey as ConfigKey] : true;

      if (!isCategoryEnabled && !isLocked) {
        continue;
      }

      if (isLocked || doBoundsIntersect(bounds, train.pathBounds)) {
        activeIds.push(trainIdStr);
      }
    }

    const oldIds = visibleTrainIdsRef.current;
    const oldIdSet = visibleTrainIdSetRef.current;
    const hasLengthChanged = activeIds.length !== oldIds.length;
    let hasContentChanged = false;

    if (!hasLengthChanged) {
      for (const id of activeIds) {
        if (!oldIdSet.has(id)) {
          hasContentChanged = true;
          break;
        }
      }
    }

    const hasChanged = hasLengthChanged || hasContentChanged;

    if (hasChanged) {
      visibleTrainIdsRef.current = activeIds;
      oldIdSet.clear();
      for (const id of activeIds) {
        oldIdSet.add(id);
      }
    }

    if (hasChanged || currentZoom.current < ZOOM_8 || !hasRenderedDataRef.current) {
      shouldForceRenderRef.current = true;
    }
  }, [config]);

  useEffect(() => {
    shouldForceRenderRef.current = true;
    updateVisibleTrainsCache();
  }, [config, updateVisibleTrainsCache]);

  useEffect(() => {
    const previousId = lockedIdRef.current;
    lockedIdRef.current = lockedJourneyId;

    if (lockedJourneyId !== previousId) {
      shouldForceRenderRef.current = true;
      updateVisibleTrainsCache();
    }
  }, [lockedJourneyId, updateVisibleTrainsCache]);

  useTrainMapEvents({
    map,
    visibleBoundsRef,
    currentZoomRef: currentZoom,
    isInteractingRef,
    shouldForceRenderRef,
    onCameraUpdate: updateVisibleTrainsCache,
  });

  const handleDataUpdate = useCallback(() => {
    const lockedIdStr = lockedIdRef.current ? String(lockedIdRef.current) : null;

    if (lockedIdStr && trainStateMap.current.has(lockedIdStr)) {
      if (!visibleTrainIdsRef.current.includes(lockedIdStr)) {
        shouldForceRenderRef.current = true;
      }
    }

    setIsSocketReady(true);

    updateVisibleTrainsCache();
  }, [updateVisibleTrainsCache]);

  useProcessSocketData({
    trainStateMapRef: trainStateMap,
    onDataUpdate: handleDataUpdate,
  });

  const { layers, trainData } = useTrainAnimationLoop({
    mapExists: !!map,
    trainStateMapRef: trainStateMap,
    visibleTrainIdsRef: visibleTrainIdsRef,
    visibleBoundsRef: visibleBoundsRef,
    currentZoomRef: currentZoom,
    lockedIdRef: lockedIdRef,
    isInteractingRef: isInteractingRef,
    shouldForceRenderRef: shouldForceRenderRef,
    // flaggedTrainIds,
  });

  useLockedTrainCleanup(lockedJourneyId, trainData, isSocketReady, trainStateMap);
  useTrainPinAnimation(mapRef, trainData, lockedJourneyId);

  return layers;
};
